import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { LoaderSymbol } from '../../components/loading';
import { useUnsavedGuard, guardedExit } from '../../hooks/useUnsavedGuard';
import { designerOrigin } from '../reporting/reportDesignerUrl';
import { dashboardFrameSrc, type DashboardFrameMode } from './dashboardDesignerUrl';

export type { DashboardFrameMode } from './dashboardDesignerUrl';

// ─────────────────────────────────────────────────────────────────────────────
// DashboardDesignerFrame — System ▸ Dashboard's "Show Dashboard Designer" view
// (ADR-0016).  The DevExpress v20.1 Web Dashboard — the SAME designer the legacy
// ALPACA LIMS (XAF) opens, over the SAME dbo.DashboardData rows — served by the
// designer host (src/AlpacaIntelligence.ReportDesigner) behind the API's
// /reports/* forwarder, embedded in an <iframe>:
//   Design → {api}/reports/dashboard/designer?id={oid}&rds={session}
//   View   → {api}/reports/dashboard/viewer?id={oid}&rds={session}
// `rds` is a DASHBOARD-scoped designer-session token from
// POST /api/v1/dashboards/designer-session (an iframe cannot send the SPA's JWT);
// the forwarder turns it into a cookie for the control's own AJAX, so it is sent
// once.  Saves are audited by the API ("Dashboard") via the host's save hook.
//
// Unsaved-changes guard (standing rule 15): the design page posts its dirty state
// ({type:'alpaca-db:state', dirty}); Back runs the shared confirm via guardedExit
// and useUnsavedGuard covers sidebar navigation / Sign Out.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  /** Dashboard Oid; null opens the designer's own "New dashboard" flow. */
  dashboardId: string | null;
  /** Title shown in the bar (the list's Title); null for New. */
  title: string | null;
  mode: DashboardFrameMode;
  onModeChange: (mode: DashboardFrameMode) => void;
  /** Return to the list (the page reloads it — a save may have added / renamed a dashboard). */
  onBack: () => void;
}

interface DesignerSession { token: string; idleSeconds: number; }

/** Messages the dashboard page posts to its host (see Views/Dashboard/Dashboard.cshtml). */
type HostMessage =
  | { type: 'alpaca-db:state'; dirty: boolean }
  | { type: 'alpaca-db:opened'; id: string };

function apiErrMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
    ?? (e instanceof Error ? e.message : fallback);
}

export function DashboardDesignerFrame({ dashboardId, title, mode, onModeChange, onBack }: Props) {
  const notify = useNotify();
  const [session, setSession] = useState<DesignerSession | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  // The dashboard actually open in the control — differs from the prop after New / Open… / Save As.
  const [openedId, setOpenedId] = useState<string | null>(dashboardId);

  // ── Dashboard-scoped designer session (one per frame) ───────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post('/api/v1/dashboards/designer-session');
        if (!cancelled) setSession((res.data as { data: DesignerSession }).data);
      } catch (e) {
        const msg = apiErrMsg(e, 'API error.');
        if (!cancelled) { setSessionError(msg); notify.error(`Could not open the dashboard designer: ${msg}`); }
      }
    })();
    return () => { cancelled = true; };
  }, [notify]);

  // ── Messages from the iframe (postMessage bridge) ───────────────────────────
  useEffect(() => {
    const origin = designerOrigin();
    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== origin) return;
      const data = ev.data as Partial<HostMessage> | null;
      if (data?.type === 'alpaca-db:state') setDirty(!!(data as { dirty?: boolean }).dirty);
      else if (data?.type === 'alpaca-db:opened') {
        const id = (data as { id?: string }).id;
        if (id) setOpenedId(id);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  // The viewer never edits; a freshly loaded frame starts clean.
  useEffect(() => { setDirty(false); }, [mode]);
  useUnsavedGuard(dirty && mode === 'design');

  // The frame follows the dashboard that is OPEN, so Design ⇄ View keeps the user's place.
  // Read through a ref: the URL is rebuilt on a mode switch only — re-keying on every
  // "opened" message would reload the designer the user is working in.
  const openedIdRef = useRef(openedId);
  openedIdRef.current = openedId;
  const src = useMemo(() => (session ? dashboardFrameSrc(mode, openedIdRef.current, session.token) : null),
    [mode, session]);

  const goBack = useCallback(() => { void guardedExit(onBack); }, [onBack]);
  const toDesign = useCallback(() => onModeChange('design'), [onModeChange]);
  const toView = useCallback(() => {
    if (!openedId) { notify.warning('Save the dashboard first to view it.'); return; }
    // Leaving the designer drops its unsaved edits — same shared confirm as Back.
    void guardedExit(() => onModeChange('view'));
  }, [openedId, onModeChange, notify]);

  // Bespoke action row → keys declared next to the buttons (standing rule 14b).
  useHotkeys([
    { combo: 'alt+p', label: mode === 'view' ? 'Design' : 'View', group: 'Dashboard Designer', order: 1,
      run: () => (mode === 'view' ? toDesign() : toView()) },
    { combo: 'esc', label: 'Back to list', group: 'Dashboard Designer', order: 2, run: goBack },
  ]);

  return (
    <div className="ddf">
      <div className="ddf-bar">
        <div className="ddf-title">
          <i className="ti ti-chart-pie" />
          <span className="ddf-name">{title || 'New Dashboard'}</span>
          <span className="ddf-sub">{mode === 'view' ? 'View' : 'Design'} · Dashboard Designer</span>
          {dirty && mode === 'design' && <span className="ddf-dirty" title="Unsaved changes in the designer">● Unsaved</span>}
        </div>
        <div className="ddf-actions">
          <div className="ddf-seg" role="group" aria-label="Dashboard mode">
            <button type="button" className={`ca-btn${mode === 'design' ? ' ca-btn-solid' : ''}`}
                    onClick={toDesign} title={mode === 'view' ? `Design this dashboard (${formatCombo('alt+p')})` : 'Design the dashboard'}>
              <i className="ti ti-pencil" /> Design
            </button>
            <button type="button" className={`ca-btn${mode === 'view' ? ' ca-btn-solid' : ''}`}
                    onClick={toView} disabled={!openedId}
                    title={openedId ? `View this dashboard (${formatCombo('alt+p')})` : 'Save the dashboard first to view it'}>
              <i className="ti ti-eye" /> View
            </button>
          </div>
          <button type="button" className="ca-btn" onClick={goBack} title={`Back to the dashboard list (${formatCombo('esc')})`}>
            <i className="ti ti-arrow-left" /> Back
          </button>
        </div>
      </div>

      <div className="ddf-frame">
        {sessionError ? (
          <div className="ddf-msg ddf-msg-err"><i className="ti ti-alert-triangle" /> {sessionError}</div>
        ) : !src ? (
          <div className="ddf-msg"><LoaderSymbol size={28} /> Opening the dashboard designer…</div>
        ) : (
          <iframe key={src} title={`Dashboard Designer — ${title || 'New Dashboard'}`} src={src} className="ddf-iframe" />
        )}
      </div>

      <style>{ddfStyles}</style>
    </div>
  );
}

const ddfStyles = `
  .ddf { display:flex; flex-direction:column; flex:1 1 auto; min-height:0; }
  .ddf-bar { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap;
    padding:8px 14px; border-bottom:1px solid var(--ai-line); background:var(--ai-header-bg); }
  .ddf-title { display:flex; align-items:center; gap:8px; min-width:0; }
  .ddf-title i { color:var(--brand-accent, #e8670a); font-size:16px; }
  .ddf-name { font-size:13px; font-weight:800; color:var(--ai-ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:40vw; }
  .ddf-sub { font-size:12px; color:var(--ai-muted); white-space:nowrap; }
  .ddf-dirty { font-size:11px; font-weight:700; color:var(--brand-accent-hover, #c04a0a);
    background:color-mix(in srgb, var(--brand-accent, #e8670a) 10%, #fff);
    border:1px solid color-mix(in srgb, var(--brand-accent, #e8670a) 30%, #fff); border-radius:12px; padding:2px 8px; }
  .ddf-actions { display:flex; align-items:center; gap:8px; }
  .ddf-seg { display:inline-flex; gap:4px; }
  .ddf-frame { flex:1 1 auto; min-height:0; position:relative; background:#fff; }
  .ddf-iframe { position:absolute; inset:0; width:100%; height:100%; border:0; display:block; }
  .ddf-msg { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; gap:10px;
    color:var(--ai-muted); font-size:13px; padding:24px; text-align:center; }
  .ddf-msg-err { color:var(--ai-red); }
`;
